import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../../store/useAuthStore';

/**
 * Login page — Depth 0 (outside Shell).
 * Renders at /login. Redirects to command-centre on success.
 * No Inter font. Uses system-ui per style guide.
 */
export const LoginPage: React.FC<{
  onNavigate: (view: string) => void;
}> = ({ onNavigate }) => {
  const { login, isLoading, error, clearError, isAuthenticated } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Read redirect param from URL
  const redirect = new URLSearchParams(window.location.search).get('redirect');

  useEffect(() => {
    if (isAuthenticated) {
      onNavigate(redirect ?? 'command-centre');
    }
  }, [isAuthenticated, onNavigate, redirect]);

  // Pre-fill email from query param (e.g., from signup 409 redirect)
  useEffect(() => {
    const prefillEmail = new URLSearchParams(window.location.search).get('email');
    if (prefillEmail) setEmail(prefillEmail);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    const success = await login(email, password);
    if (success) {
      onNavigate(redirect ?? 'command-centre');
    }
  };

  const isLocked = error?.code === 'account_locked';
  const isRateLimited = error?.code === 'rate_limited';
  const formDisabled = isLoading || isLocked || isRateLimited;

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ backgroundColor: 'var(--bg-primary)', fontFamily: 'system-ui, sans-serif' }}
    >
      <div
        className="w-full max-w-sm rounded-lg p-6"
        style={{
          backgroundColor: 'var(--bg-secondary)',
          border: '1px solid var(--border-default)',
        }}
      >
        <h1
          className="text-xl font-semibold mb-6 text-center"
          style={{ color: 'var(--text-primary)' }}
        >
          Log in to your company
        </h1>

        {error && (
          <div
            role="alert"
            aria-live="assertive"
            className="mb-4 rounded-md p-3 text-sm"
            style={{
              backgroundColor: 'rgba(220, 38, 38, 0.1)',
              border: '1px solid rgba(220, 38, 38, 0.3)',
              color: '#fca5a5',
            }}
          >
            {isLocked
              ? `Your account is temporarily locked due to too many failed attempts. Try again in 1 hour.`
              : isRateLimited
                ? `Too many login attempts. Please wait before trying again.`
                : error.message}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="mb-4">
            <label
              htmlFor="login-email"
              className="block text-sm mb-1"
              style={{ color: 'var(--text-secondary)' }}
            >
              Email
            </label>
            <input
              id="login-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={formDisabled}
              placeholder="founder@company.com"
              required
              className="w-full rounded-md px-3 py-2 text-sm outline-none transition-colors"
              style={{
                backgroundColor: 'var(--bg-elevated)',
                border: '1px solid var(--border-default)',
                color: 'var(--text-primary)',
              }}
              aria-describedby={error ? 'login-error' : undefined}
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="login-password"
              className="block text-sm mb-1"
              style={{ color: 'var(--text-secondary)' }}
            >
              Password
            </label>
            <div className="relative">
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={formDisabled}
                placeholder="Enter your password"
                required
                className="w-full rounded-md px-3 py-2 pr-10 text-sm outline-none transition-colors"
                style={{
                  backgroundColor: 'var(--bg-elevated)',
                  border: '1px solid var(--border-default)',
                  color: 'var(--text-primary)',
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1"
                style={{ color: 'var(--text-tertiary)' }}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? '🙈' : '👁'}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={formDisabled || !email || !password}
            className="w-full rounded-md py-2.5 text-sm font-medium transition-opacity disabled:opacity-50"
            style={{
              backgroundColor: 'var(--accent-amber)',
              color: '#0E1420',
            }}
          >
            {isLoading ? 'Logging in...' : 'Log In'}
          </button>
        </form>

        <p
          className="mt-4 text-center text-sm"
          style={{ color: 'var(--text-tertiary)' }}
        >
          Don't have an account?{' '}
          <button
            onClick={() => onNavigate('signup')}
            className="underline"
            style={{ color: 'var(--accent-teal)' }}
          >
            Sign up
          </button>
        </p>
      </div>
    </div>
  );
};
